import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { catalogFeatures, heroFeatures, spotlightFeatures, visibleFeatures } from './data/features';
import { updates } from './data/updates';
import { isFeatureCatalogVisible } from './data/announcement';
import { IFeature } from './data/types';
import { pageBackground } from './styles';
import { trackFeatureInterest } from 'api/app/feature-interest.api';
import CatalogSection from './components/CatalogSection';
import ContactBand from './components/ContactBand';
import FeatureDialog from './components/FeatureDialog';
import HeroSection from './components/HeroSection';
import ReturnBar from './components/ReturnBar';
import SpotlightSection from './components/SpotlightSection';
import UpdatesTimeline from './components/UpdatesTimeline';

interface MainPageLocationState {
    whatsNewReturnTo?: string;
}

const MainPage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [selectedFeature, setSelectedFeature] = useState<IFeature | null>(null);
    const [returnTo, setReturnTo] = useState(() => (location.state as MainPageLocationState | null)?.whatsNewReturnTo ?? '');

    const clearReturnTo = useCallback(() => {
        setReturnTo('');
        navigate(location.pathname, { replace: true, state: null });
    }, [location.pathname, navigate]);

    const openFeature = useCallback((feature: IFeature) => {
        setSelectedFeature(feature);
        trackFeatureInterest(feature.id);
    }, []);

    const openFeatureById = useCallback((featureId: string) => {
        const match = visibleFeatures.find((feature) => feature.id === featureId);
        if (match) openFeature(match);
    }, [openFeature]);

    const showCatalog = isFeatureCatalogVisible();

    return (
        <div className="main-page" dir="rtl">
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    minHeight: '100%',
                    minWidth: 0,
                    overflowX: 'hidden',
                    px: { xs: 2, md: 4 },
                    py: { xs: 2, md: 4 },
                    gap: { xs: 4, md: 5 },
                    background: pageBackground,
                    textAlign: 'start',
                    '& .MuiTypography-root': { textAlign: 'start' }
                }}
            >
                {showCatalog && returnTo && <ReturnBar returnTo={returnTo} onContinue={() => navigate(returnTo)} onDismiss={clearReturnTo} />}

                {showCatalog && <HeroSection features={heroFeatures} onOpen={openFeature} />}

                {showCatalog && <SpotlightSection features={spotlightFeatures} onOpen={openFeature} />}

                {showCatalog && <CatalogSection features={catalogFeatures} onOpen={openFeature} />}

                <UpdatesTimeline updates={updates} onOpenFeature={openFeatureById} />

                {showCatalog && <ContactBand />}
            </Box>

            {showCatalog && <FeatureDialog feature={selectedFeature} onClose={() => setSelectedFeature(null)} />}
        </div>
    );
};

export default MainPage;
